/**
 * 分举报类型趋势组件
 * 设计稿要求：
 * - 标题 + 联动说明文字
 * - 深色背景 tooltip
 * - 指定颜色：外挂类(#FF3B30)、语言/文字违规(#FF9500)、消极行为(#FC0)
 * - 选中交互：选中系列高亮，其他变淡
 */
import React, { useMemo, useState, useCallback, useContext } from 'react';
import CardCommon from '@op/components/CardCommon';
import { pieIcon, barIcon, lineIcon, tableIcon } from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_U } from '@op/utils/format';
import { useCategoryMentionsTrend } from '../../../hooks/useCategoryMentionsTrend';
import { useEventData } from '@op/hooks/useEventData';
import { formatContributionText } from '../../../utils/calculations';
import TrendChart from './TrendChart';
import PieChart from './PieChart';
import TableChart from './TableChart';
import './index.scss';
import { SecurityWeeklyReportContext } from '@op/pages/SecurityWeeklyReport/context';

interface IReportTypeTrendProps {
  filter: any;
  selectedType: string | null;
  onTypeClick: (type: string) => void;
}

const ReportTypeTrend: React.FC<IReportTypeTrendProps> = ({
  filter,
  selectedType,
  onTypeClick,
}) => {
  const { formatMessage, isEn } = useLanguage();
  const [chartType, setChartType] = useState<'line' | 'bar' | 'pie' | 'table'>(
    'line',
  );

  // 使用 useCategoryMentionsTrend hook，获取图表数据
  const { loading, lineChartData, pieChartData, summary, list } =
    useCategoryMentionsTrend(filter);
  // 获取事件数据（仅折线图和柱状图需要）
  const { eventData, loading: eventLoading } = useEventData(
    filter,
    {
      xAxisData: lineChartData?.xAxis,
      format: 'YYYY-MM-DD',
    },
    { autoFetch: chartType === 'line' || chartType === 'bar' },
  );

  // 图表点击事件 - 饼图和其他图表 params 结构不同
  const handleChartClick = useCallback(
    (params: any) => {
      const clickedName = chartType === 'pie' ? params.name : params.seriesName;
      if (clickedName) {
        onTypeClick(clickedName);
      }
    },
    [onTypeClick, chartType],
  );

  // 波动贡献率描述 - 使用通用函数
  const contributionText = useMemo(() => {
    if (!summary || !filter) return '';
    const topCategoryName = isEn
      ? summary.top_category_name_en
      : summary.top_category_name;
    return formatContributionText({
      categoryName: topCategoryName,
      changeRate: summary.change_rate,
      contributionRate: summary.contribution_rate,
      formatMessage,
    });
  }, [summary, formatMessage, isEn, filter]);

  // 下载处理
  const handleDownload = () => {
    if (!lineChartData?.tableRecord) return;
    DBO_U.download({
      key: 'WeeklyReportType',
      fileName: 'Weekly Report - Report Type Trend',
      filter,
      data: lineChartData.tableRecord,
      auxiliaryParams: { filter, columns: lineChartData.legend },
    });
  };

  const { toFeeds } = useContext(SecurityWeeklyReportContext);

  // 根据图表类型渲染对应组件
  const renderContent = () => {
    if (chartType === 'table') {
      return (
        <TableChart
          data={lineChartData}
          height={300.5}
          isEn={isEn}
          list={list}
        />
      );
    }

    if (chartType === 'pie') {
      return (
        <PieChart
          data={pieChartData}
          selectedType={selectedType}
          onChartClick={handleChartClick}
          isEn={isEn}
          list={list}
        />
      );
    }

    // line, bar
    return (
      <TrendChart
        data={lineChartData}
        chartType={chartType}
        selectedType={selectedType}
        onChartClick={handleChartClick}
        filter={filter}
        isEn={isEn}
        list={list}
        toFeeds={toFeeds}
        eventData={eventData}
      />
    );
  };

  return (
    <CardCommon
      className="dbo-report-type-trend"
      title={
        <span className="dbo-report-type-trend__title-wrapper">
          {formatMessage({ id: 'Mentions by Topic' })}
          <span className="dbo-report-type-trend__title-hint">
            {formatMessage({
              id: '(Chart Interaction: Report Type → Subtopic → Keyword) Click a line to filter linked subtopics and keywords',
            })}
          </span>
        </span>
      }
      subTitle={contributionText}
      operations={[pieIcon, barIcon, lineIcon, tableIcon]}
      defaultOperation={chartType}
      handleOperationsOnClick={(item) => {
        setChartType(item?.symbol as 'line');
      }}
      loading={loading || eventLoading}
      empty={lineChartData?.isEmpty}
      loadingHeight={350}
      emptyHeight={350}
      hasDownload={!lineChartData?.isEmpty}
      handleDownloadOnClick={handleDownload}
      logBtnId="WR_ReportType"
      contentCard
    >
      {renderContent()}
    </CardCommon>
  );
};

export default ReportTypeTrend;
